import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { trans, withClientConfig } from '@spotahome/soyuz/client';
import { Heading, CloseButton } from '@spotahome/ui-library';

import '@spotahome/ui-library/lib/components/Heading.css';

import { getCityName } from '@utils/cities';
import getGqlClient from '@gqlClient/client';
import { getAreasQuery } from '@common/graphql/queries/areas';

import AlertModalContainer from '@components/AlertModalContainer';

import AlertShape from '@alerts/shapes/alert';

import AlertCityArea from './AlertCityArea';
import CityAutocompleter from './CityAutocompleter';
import AlertDatePicker from './AlertDatePicker';
import AlertPriceRange from './AlertPriceRange';
import AlertPropertyTypes from './AlertPropertyTypes';

import './AlertModal.scss';

const INITIAL_MEDIUMS = ['email'];

const getInitialState = ({ alert, cities }) => {
  const { cityId } = alert;
  const selectedCity = cities[cityId] || {};

  return {
    cityId,
    errors: {},
    hasError: false,
    currencyIsoCode: selectedCity.currencyIsoCode,
    moveIn: alert.moveIn,
    moveOut: alert.moveOut,
    minPrice: alert.minPrice,
    maxPrice: alert.maxPrice,
    propertyTypes: alert.propertyTypes,
    cityAreaIds: alert.cityAreaIds || [],
    mediums: INITIAL_MEDIUMS
  };
};

const gatherDataToSend = ({
  cityId,
  moveIn,
  moveOut,
  minPrice,
  maxPrice,
  propertyTypes,
  mediums,
  cityAreaIds
}) => ({
  cityId,
  moveIn,
  moveOut,
  minPrice,
  maxPrice,
  propertyTypes,
  mediums,
  cityAreaIds
});

const getAreas = async city => {
  const areas = await getAreasQuery(getGqlClient())(city);

  return areas;
};

class AlertModal extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    cancel: PropTypes.func.isRequired,
    confirm: PropTypes.func.isRequired,
    cities: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        name: PropTypes.string,
        currencyIsoCode: PropTypes.string
      })
    }).isRequired,
    alert: PropTypes.shape(AlertShape),
    isLoading: PropTypes.bool.isRequired,
    cityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    clientConfig: PropTypes.shape({
      featureToggle: PropTypes.shape({
        alertModalCityAreaFilter: PropTypes.bool.isRequired
      })
    })
  };

  static defaultProps = {
    alert: {
      cityId: '',
      minPrice: '',
      maxPrice: '',
      propertyTypes: [],
      cityAreaIds: [],
      mediums: INITIAL_MEDIUMS
    },
    clientConfig: {
      featureToggle: {
        alertModalCityAreaFilter: false
      }
    }
  };

  state = { ...getInitialState(this.props), areas: [] };

  componentWillReceiveProps = async nextProps => {
    const nextCity = getCityName(nextProps.alert.cityId);
    let newState = { ...getInitialState(nextProps) };

    if (nextCity && nextCity !== this.value) {
      const updatedAreasState = await this.getUpdatedAreasState(
        nextProps.alert.cityId,
        nextCity
      );
      newState = { ...newState, ...updatedAreasState };
    }

    this.setState(newState);
  };

  handleCityChange = async (newCityData, cityName) => {
    const updatedAreasState = await this.getUpdatedAreasState(
      newCityData.cityId,
      cityName
    );

    this.setState({ ...newCityData, ...updatedAreasState }, this.checkErrors);
  };

  getUpdatedAreasState = async (cityId, cityName) => {
    if (this.value === cityName) {
      return {};
    }
    this.value = cityName;

    const areas = await getAreas(cityId);

    return {
      areas: areas.data.areas,
      cityAreaIds: this.props.alert.cityAreaIds
    };
  };

  handleDateChange = newDates => {
    this.setState(newDates);
  };

  handlePriceChange = newPrices => {
    this.setState(newPrices, this.checkErrors);
  };

  handlePropertyTypeChange = propertyTypes => {
    this.setState({ propertyTypes });
  };

  getErrors = () => ({
    autocompleter: !!this.hasErrorCity(),
    priceRange: !!this.hasErrorPriceRange()
  });

  handleCityAreaChange = cityAreaIds => {
    this.setState({ cityAreaIds });
  };

  cleanUp = () => {
    this.value = '';
    this.setState({ areas: [] });
  };

  hasErrorPriceRange = () =>
    this.state.minPrice &&
    this.state.maxPrice &&
    this.state.minPrice > this.state.maxPrice;

  handleClose = () => {
    this.props.cancel();
    this.cleanUp();
  };

  onConfirm = () => {
    const hasError = this.checkErrors();

    if (!hasError) {
      this.props.confirm(gatherDataToSend(this.state));
    }
    this.cleanUp();
  };

  handleChange = value => {
    this.value = value;
  };

  checkErrors = () => {
    const errors = this.getErrors();
    const hasError = Object.keys(errors).filter(key => errors[key]).length;

    this.setState({ hasError: !!hasError, errors });

    return hasError;
  };

  hasErrorCity = () =>
    !this.state.cityId || !this.props.cityNames.includes(this.value);

  render() {
    const {
      areas,
      cityId,
      hasError,
      moveIn,
      moveOut,
      minPrice,
      maxPrice,
      propertyTypes,
      cityAreaIds,
      currencyIsoCode,
      errors
    } = this.state;

    const hasToggleCityAreaFilter = this.props.clientConfig.featureToggle
      .alertModalCityAreaFilter;

    const showAreas =
      hasToggleCityAreaFilter && cityId && areas && areas.length;

    const alertModalGridClassNames = `add-edit-form__grid add-edit-form--with${
      showAreas ? '' : 'out'
    }-neighborhoods`;

    return (
      <AlertModalContainer
        isOpen={this.props.isOpen}
        cancel={this.props.cancel}
        accept={this.onConfirm}
        hasError={hasError}
        isLoading={this.props.isLoading}
      >
        <div data-test="addEditAlertModal" className="add-edit-form__header">
          <CloseButton
            size="small"
            className="close-button__button"
            onClose={this.handleClose}
          />
          <Heading
            level={4}
            theme="dark"
            size="medium"
            className="add-edit-form__title"
            text={trans('alerts.modal.title')}
          />
        </div>
        <div className={alertModalGridClassNames}>
          <CityAutocompleter
            cities={this.props.cities}
            cityName={this.value}
            cityId={cityId}
            cityNames={this.props.cityNames}
            onSelectedElement={this.handleCityChange}
            onChange={this.handleChange}
            hasError={errors.autocompleter}
          />
          <AlertDatePicker
            onChange={this.handleDateChange}
            moveIn={moveIn}
            moveOut={moveOut}
          />
          <AlertPriceRange
            minPrice={minPrice}
            maxPrice={maxPrice}
            currencyIsoCode={currencyIsoCode}
            onChange={this.handlePriceChange}
            hasError={errors.priceRange}
          />
          <AlertPropertyTypes
            propertyTypes={propertyTypes}
            onChange={this.handlePropertyTypeChange}
          />
          {showAreas && (
            <AlertCityArea
              cityAreas={areas}
              cityAreaIds={cityAreaIds}
              onChange={this.handleCityAreaChange}
            />
          )}
        </div>
      </AlertModalContainer>
    );
  }
}

export default withClientConfig()(AlertModal);
